import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { OEMProfile } from "../../../types/Profiles/oemProfile.types";
import { updateOEMIndustrial } from "../../../services/oemProfile/oemProfile";
import { useTranslate } from "../../../hooks/LanguageProvider";

import {
  FaIndustry,
  FaGears,
  FaBoxesStacked,
  FaSitemap,
  FaPen,
} from "react-icons/fa6";
import { toast } from "react-toastify";

interface Props {
  profile: OEMProfile;
  onProfileUpdated?: () => void;
}

type IndustrialFormState = {
  plants: number;
  assemblyLines: number;
  productionCapacity: number;
  sopPrograms: number;
};

export default function IndustrialTab({ profile, onProfileUpdated }: Props) {
  const { translate } = useTranslate();

  const [showEditModal, setShowEditModal] = useState(false);

  const [industrialForm, setIndustrialForm] = useState<IndustrialFormState>({
    plants: profile.industrialPresence.plants ?? 0,
    assemblyLines: profile.industrialPresence.assemblyLines ?? 0,
    productionCapacity: profile.industrialPresence.productionCapacity ?? 0,
    sopPrograms: profile.industrialPresence.sopPrograms ?? 0,
  });

  useEffect(() => {
    setIndustrialForm({
      plants: profile.industrialPresence.plants ?? 0,
      assemblyLines: profile.industrialPresence.assemblyLines ?? 0,
      productionCapacity: profile.industrialPresence.productionCapacity ?? 0,
      sopPrograms: profile.industrialPresence.sopPrograms ?? 0,
    });
  }, [profile]);

  const handleOpenModal = () => {
    setIndustrialForm({
      plants: profile.industrialPresence.plants ?? 0,
      assemblyLines: profile.industrialPresence.assemblyLines ?? 0,
      productionCapacity: profile.industrialPresence.productionCapacity ?? 0,
      sopPrograms: profile.industrialPresence.sopPrograms ?? 0,
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => setShowEditModal(false);

  const handleNumberChange =
    (field: keyof IndustrialFormState) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);
      setIndustrialForm((prev) => ({
        ...prev,
        [field]: Number.isNaN(value) ? 0 : value,
      }));
    };

  const handleSave = async () => {
    try {
      await updateOEMIndustrial(industrialForm);
      toast.success(translate("industrial_update_success"));
      setShowEditModal(false);
      onProfileUpdated?.();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.error || translate("industrial_update_error")
      );
    }
  };

  const cards = useMemo(
    () => [
      {
        key: "plants",
        label: translate("plants"),
        value: industrialForm.plants,
        icon: <FaIndustry />,
        iconBg: "#fff3cd",
        iconColor: "#f4b400",
        badgeBg: "#fff8e1",
        badgeColor: "#b78103",
      },
      {
        key: "assemblyLines",
        label: translate("assembly_lines"),
        value: industrialForm.assemblyLines,
        icon: <FaGears />,
        iconBg: "#e7f1ff",
        iconColor: "#0d6efd",
        badgeBg: "#eef5ff",
        badgeColor: "#0b5ed7",
      },
      {
        key: "productionCapacity",
        label: translate("production_capacity"),
        value: industrialForm.productionCapacity.toLocaleString(),
        icon: <FaBoxesStacked />,
        iconBg: "#e8f5e9",
        iconColor: "#198754",
        badgeBg: "#eefaf1",
        badgeColor: "#146c43",
      },
      {
        key: "sopPrograms",
        label: translate("sop_programs"),
        value: industrialForm.sopPrograms,
        icon: <FaSitemap />,
        iconBg: "#f3e8ff",
        iconColor: "#7c3aed",
        badgeBg: "#f7efff",
        badgeColor: "#6f42c1",
      },
    ],
    [industrialForm, translate]
  );

  return (
    <>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h4 className="fw-semibold mb-1">{translate("industrial_capacity")}</h4>
              <p className="text-muted mb-0">
                {translate("industrial_capacity_desc")}
              </p>
            </div>

            <div>
              <Button
                variant="warning"
                className="rounded-3 px-4 fw-semibold d-inline-flex align-items-center gap-2"
                onClick={handleOpenModal}
              >
                <FaPen size={14} />
                {translate("edit_industrial_capacity")}
              </Button>
            </div>
          </div>

          <div className="row g-4">
            {cards.map((card) => (
              <div className="col-xl-3 col-md-6" key={card.key}>
                <div
                  className="border rounded-4 p-4 h-100"
                  style={{
                    background: "#fff",
                    minHeight: 185,
                    transition: "all 0.25s ease",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-4"
                      style={{
                        width: 58,
                        height: 58,
                        backgroundColor: card.iconBg,
                        color: card.iconColor,
                        fontSize: 22,
                      }}
                    >
                      {card.icon}
                    </div>

                    <span
                      className="badge rounded-pill fw-semibold"
                      style={{
                        backgroundColor: card.badgeBg,
                        color: card.badgeColor,
                        padding: "0.45rem 0.75rem",
                      }}
                    >
                      {translate("kpi")}
                    </span>
                  </div>

                  <h2 className="fw-bold mb-1">{card.value}</h2>
                  <h6 className="fw-semibold mb-0">{card.label}</h6>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal
        show={showEditModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-semibold">
            {translate("edit_industrial_capacity")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-3">
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-semibold">{translate("plants")}</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={industrialForm.plants}
                  onChange={handleNumberChange("plants")}
                  className="rounded-3"
                />
                <Form.Text className="text-muted">
                  {translate("number_of_plants_desc")}
                </Form.Text>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  {translate("assembly_lines")}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={industrialForm.assemblyLines}
                  onChange={handleNumberChange("assemblyLines")}
                  className="rounded-3"
                />
                <Form.Text className="text-muted">
                  {translate("assembly_lines_desc")}
                </Form.Text>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  {translate("production_capacity")}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={industrialForm.productionCapacity}
                  onChange={handleNumberChange("productionCapacity")}
                  className="rounded-3"
                />
                <Form.Text className="text-muted">
                  {translate("production_capacity_desc")}
                </Form.Text>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  {translate("sop_programs")}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={industrialForm.sopPrograms}
                  onChange={handleNumberChange("sopPrograms")}
                  className="rounded-3"
                />
                <Form.Text className="text-muted">
                  {translate("sop_programs_desc")}
                </Form.Text>
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={handleCloseModal}
            className="rounded-3 px-4"
          >
            {translate("cancel")}
          </Button>
          <Button
            variant="warning"
            onClick={handleSave}
            className="rounded-3 px-4 fw-semibold"
          >
            {translate("save_changes")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}